import { useMemo } from 'react';
import { AgileMacroplanogram } from '../types/AgileMacroplanogramTypes';
import { MacroData, SubcategoryAllocation } from '../types/zoneMapType';
import { FloorplanData } from '../zoneViewer/zoneMapDataType';
import { FloorplanFixtureData } from '../zoneViewer/zoneMapDataType';
import buildFixtureIdToZoneDataMap, { generateZoneNameKeyMap } from '../zoneViewer/zoneMapDataHelper';
import { buildPickPathFromOrdering } from '../pickPath/buildPickPathFromOrdering';
import { buildOrderedAllocations } from '../allocation/buildOrderedAllocations';
import { computeFixtureAllocations, FixtureAssignment } from '../allocation/computeFixtureAllocations';
import { buildFixturePositionMap } from '../allocation/buildFixturePositionMap';
import { colorByKey, subcategoryColorByIndex } from '../zoneViewer/zoneMapColorHelper';

interface UseViewModeDataInput {
  zoneNameList: MacroData[];
  macroplanogramList: AgileMacroplanogram[] | null | undefined;
  selectedDepartment: string;
  agileFixtures: FloorplanFixtureData[];
  fixtureOrderingMap: Map<string, number>;
  fpData: FloorplanData[];
}

export function useViewModeData({
  zoneNameList,
  macroplanogramList,
  selectedDepartment,
  agileFixtures,
  fixtureOrderingMap,
  fpData,
}: UseViewModeDataInput) {
  const filteredZoneNameList = useMemo(() => {
    if (selectedDepartment && agileFixtures.length > 0) {
      const departmentLinkId = selectedDepartment.toLowerCase().trim();
      const departmentFixtureIds = new Set(agileFixtures.map((f) => f.id).filter(Boolean));
      return zoneNameList.filter((zone) => {
        if (zone.linkId) return zone.linkId === departmentLinkId;
        return zone.unParsedFixtureIds?.some((fId) => departmentFixtureIds.has(fId));
      });
    }
    return zoneNameList;
  }, [zoneNameList, selectedDepartment, agileFixtures]);

  const zoneNameKeyMap = useMemo(() => generateZoneNameKeyMap(filteredZoneNameList), [filteredZoneNameList]);

  const fixtureIdToZoneDataMap = useMemo(
    () => buildFixtureIdToZoneDataMap(filteredZoneNameList, fpData, zoneNameKeyMap),
    [filteredZoneNameList, fpData, zoneNameKeyMap],
  );

  const viewPickPathLayers = useMemo(
    () => buildPickPathFromOrdering(fixtureOrderingMap, fpData),
    [fixtureOrderingMap, fpData],
  );

  const viewAllocations: SubcategoryAllocation[] = useMemo(() => {
    const departmentLinkId = selectedDepartment ? selectedDepartment.toLowerCase().trim() : null;

    if (!selectedDepartment && macroplanogramList) {
      const macros = macroplanogramList.filter((m) => (m.fixtureIds?.length ?? 0) > 0);
      return macros.map((macro, idx) => {
        // GFC Drop Zone identity is categoryId (stable across renames). Falls
        // back to entryId for legacy macros that predate first-class categoryId,
        // and to subcategoryColorByIndex for macros without categoryId (the
        // WFM/Fresh empty-department view keys off list position instead).
        const key = macro.categoryId ?? macro.entryId;
        return {
          subcategoryId: key,
          subcategoryName: macro.macroplanogramName || macro.linkId || macro.entryId,
          percentage: 0,
          color: macro.categoryId ? colorByKey(macro.categoryId) : subcategoryColorByIndex(idx),
        };
      });
    }

    const sourceMacro = macroplanogramList?.find(
      (macro) =>
        macro.storeSubcategories?.some((sc) => sc.capacityPercentage != null && sc.capacityPercentage > 0) &&
        (departmentLinkId == null || macro.linkId === departmentLinkId || macro.linkId == null),
    );
    if (!sourceMacro) return [];

    return buildOrderedAllocations(sourceMacro.storeSubcategories ?? [], fixtureOrderingMap);
  }, [macroplanogramList, selectedDepartment, fixtureOrderingMap]);

  const fixturePositionMap = useMemo(() => buildFixturePositionMap(fpData), [fpData]);

  const viewFixtureAssignments = useMemo(() => {
    if (viewAllocations.length === 0) return undefined;

    const colorMap = new Map<string, { name: string; color: string }>();
    for (const alloc of viewAllocations) {
      colorMap.set(alloc.subcategoryId, { name: alloc.subcategoryName, color: alloc.color });
    }

    if (!selectedDepartment && macroplanogramList) {
      const macros = macroplanogramList.filter((m) => (m.fixtureIds?.length ?? 0) > 0);
      const assignments: FixtureAssignment[] = [];
      for (const macro of macros) {
        const key = macro.categoryId ?? macro.entryId;
        const info = colorMap.get(key);
        if (!info) continue;
        for (const fixtureId of macro.fixtureIds ?? []) {
          assignments.push({
            fixtureId,
            sequenceOrder: 0,
            subcategoryId: key,
            subcategoryName: info.name,
            color: info.color,
            isBoundary: false,
          });
        }
      }
      return assignments.length > 0 ? assignments : undefined;
    }

    if (!fixtureOrderingMap || fixtureOrderingMap.size === 0) return undefined;
    const fixtureSequence = Array.from(fixtureOrderingMap.entries()).map(([fixtureId, sequenceOrder]) => ({
      fixtureId,
      sequenceOrder,
    }));
    const assignments = computeFixtureAllocations(viewAllocations, fixtureSequence, fixturePositionMap);
    return assignments.length > 0 ? assignments : undefined;
  }, [viewAllocations, fixtureOrderingMap, selectedDepartment, macroplanogramList, fixturePositionMap]);

  return {
    filteredZoneNameList,
    fixtureIdToZoneDataMap,
    viewPickPathLayers,
    viewAllocations,
    viewFixtureAssignments,
  };
}
